/**
 * Curated map of exercise-name → open-source photo URL.
 *
 * Source: yuhonas/free-exercise-db on GitHub (MIT licensed).
 * Direct raw-content URLs are stable and CDN-cached by GitHub.
 * Only the ~40 most common lifts from BRETH's seed catalogue are mapped
 * here — everything else falls back to the stylised muscle-group SVG.
 *
 * If you add a new lift to the seed catalogue and it needs a photo, drop
 * another entry below. Prefer a `.jpg` from the same repo when possible.
 */
const GH = 'https://raw.githubusercontent.com/yuhonas/free-exercise-db/main/exercises';

const EXERCISE_PHOTOS = {
  // Chest
  'Barbell Bench Press':          `${GH}/Barbell_Bench_Press_-_Medium_Grip/0.jpg`,
  'Bench Press':                  `${GH}/Barbell_Bench_Press_-_Medium_Grip/0.jpg`,
  'Dumbbell Bench Press':         `${GH}/Dumbbell_Bench_Press/0.jpg`,
  'Incline Barbell Bench Press':  `${GH}/Barbell_Incline_Bench_Press_-_Medium-Grip/0.jpg`,
  'Incline Dumbbell Press':       `${GH}/Incline_Dumbbell_Press/0.jpg`,
  'Push Up':                      `${GH}/Pushups/0.jpg`,
  'Push-Up':                      `${GH}/Pushups/0.jpg`,
  'Push Ups':                     `${GH}/Pushups/0.jpg`,
  'Cable Crossover':              `${GH}/Cable_Crossover/0.jpg`,
  'Chest Fly':                    `${GH}/Dumbbell_Flyes/0.jpg`,
  'Dumbbell Fly':                 `${GH}/Dumbbell_Flyes/0.jpg`,

  // Back
  'Deadlift':                     `${GH}/Barbell_Deadlift/0.jpg`,
  'Barbell Deadlift':             `${GH}/Barbell_Deadlift/0.jpg`,
  'Romanian Deadlift':            `${GH}/Romanian_Deadlift/0.jpg`,
  'Pull-Up':                      `${GH}/Pullups/0.jpg`,
  'Pull Up':                      `${GH}/Pullups/0.jpg`,
  'Chin-Up':                      `${GH}/Chin-Up/0.jpg`,
  'Barbell Row':                  `${GH}/Bent_Over_Barbell_Row/0.jpg`,
  'Bent-Over Row':                `${GH}/Bent_Over_Barbell_Row/0.jpg`,
  'Dumbbell Row':                 `${GH}/One-Arm_Dumbbell_Row/0.jpg`,
  'Lat Pulldown':                 `${GH}/Wide-Grip_Lat_Pulldown/0.jpg`,
  'Seated Cable Row':             `${GH}/Seated_Cable_Rows/0.jpg`,

  // Shoulders
  'Overhead Press':               `${GH}/Standing_Military_Press/0.jpg`,
  'Barbell Overhead Press':       `${GH}/Standing_Military_Press/0.jpg`,
  'Military Press':               `${GH}/Standing_Military_Press/0.jpg`,
  'Dumbbell Shoulder Press':      `${GH}/Dumbbell_Shoulder_Press/0.jpg`,
  'Lateral Raise':                `${GH}/Side_Lateral_Raise/0.jpg`,
  'Dumbbell Lateral Raise':       `${GH}/Side_Lateral_Raise/0.jpg`,
  'Front Raise':                  `${GH}/Front_Dumbbell_Raise/0.jpg`,
  'Face Pull':                    `${GH}/Face_Pull/0.jpg`,

  // Arms
  'Barbell Curl':                 `${GH}/Barbell_Curl/0.jpg`,
  'Dumbbell Curl':                `${GH}/Dumbbell_Bicep_Curl/0.jpg`,
  'Hammer Curl':                  `${GH}/Hammer_Curls/0.jpg`,
  'Tricep Pushdown':              `${GH}/Triceps_Pushdown/0.jpg`,
  'Cable Tricep Pushdown':        `${GH}/Triceps_Pushdown/0.jpg`,
  'Skull Crusher':                `${GH}/EZ-Bar_Skullcrusher/0.jpg`,
  'Close-Grip Bench Press':       `${GH}/Bench_Press_-_Close_Grip/0.jpg`,

  // Legs
  'Barbell Squat':                `${GH}/Barbell_Squat/0.jpg`,
  'Back Squat':                   `${GH}/Barbell_Squat/0.jpg`,
  'Front Squat':                  `${GH}/Barbell_Front_Squat/0.jpg`,
  'Goblet Squat':                 `${GH}/Goblet_Squat/0.jpg`,
  'Leg Press':                    `${GH}/Leg_Press/0.jpg`,
  'Bulgarian Split Squat':        `${GH}/Dumbbell_Rear_Lunge/0.jpg`,
  'Lunge':                        `${GH}/Dumbbell_Lunges/0.jpg`,
  'Walking Lunge':                `${GH}/Dumbbell_Walking_Lunge/0.jpg`,
  'Leg Curl':                     `${GH}/Lying_Leg_Curls/0.jpg`,
  'Leg Extension':                `${GH}/Leg_Extensions/0.jpg`,
  'Calf Raise':                   `${GH}/Standing_Calf_Raises/0.jpg`,
  'Hip Thrust':                   `${GH}/Barbell_Hip_Thrust/0.jpg`,

  // Core
  'Plank':                        `${GH}/Plank/0.jpg`,
  'Hanging Leg Raise':            `${GH}/Hanging_Leg_Raise/0.jpg`,
  'Sit-Up':                       `${GH}/Sit-Up/0.jpg`,
  'Crunch':                       `${GH}/Crunches/0.jpg`,
};

/** Case-insensitive lookup that also strips a leading "Barbell/Dumbbell" prefix
 *  before giving up, so "Cable Bench Press" hits "Bench Press" if we haven't
 *  mapped the cable variant explicitly. */
export function photoForExercise(name) {
  if (!name) return null;
  const direct = EXERCISE_PHOTOS[name];
  if (direct) return direct;
  const norm = name.trim();
  for (const k of Object.keys(EXERCISE_PHOTOS)) {
    if (k.toLowerCase() === norm.toLowerCase()) return EXERCISE_PHOTOS[k];
  }
  // Strip a leading equipment word and retry.
  const stripped = norm.replace(/^(Barbell|Dumbbell|Cable|Machine|Kettlebell|Smith Machine)\s+/i, '');
  if (stripped !== norm) return photoForExercise(stripped);
  return null;
}
